import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';

/**
 * HashtagInputDirective
 *
 * Two modes, controlled by [hashtagMode]:
 *
 *  "dedicated" (default) — the entire input IS the hashtag field.
 *    Any typing triggers autocomplete. User doesn't need to type '#'.
 *    e.g. the standalone "Add hashtags" input in post / event forms.
 *
 *  "inline" — the input is free text (e.g. a caption) that may contain
 *    hashtags. Only triggers after the user types '#'.
 *
 * Events:
 *   hashtagSearch   – partial string to search (e.g. "lon")
 *   hashtagDropdown – true/false open/close signal
 *   valueChange     – raw input value on every keystroke
 *
 * Template ref (exportAs: 'appHashtagInput') gives access to
 * insertHashtag(tag) to replace partial text with the chosen tag.
 */
@Directive({
  selector:   '[appHashtagInput]',
  standalone: true,
  exportAs:   'appHashtagInput',
})
export class HashtagInputDirective {

  /** 'dedicated' = standalone hashtag input; 'inline' = free-text with # trigger */
  @Input() hashtagMode: 'dedicated' | 'inline' = 'dedicated';

  @Output() hashtagSearch   = new EventEmitter<string>();
  @Output() hashtagDropdown = new EventEmitter<boolean>();
  @Output() valueChange     = new EventEmitter<string>();

  /**
   * Fires when the user types a separator (space or comma) that should commit
   * the tag typed so far — the same thing `(keydown.space)` / `(keydown.comma)`
   * do on desktop.
   *
   * Needed because those bindings match on `event.key`, and mobile virtual
   * keyboards route text through an IME: `keydown` arrives as
   * `key: "Unidentified"` / `keyCode: 229` (or not per-character at all), so the
   * key bindings never match and the tag was never added. `input` always fires,
   * so the separator is detected from the value instead.
   *
   * Emitted asynchronously so `ngModel` has written the new value back to the
   * component before the handler reads it — host listener order relative to
   * Angular's own value accessor is not guaranteed.
   */
  @Output() separatorTyped = new EventEmitter<Event>();

  /** Offset where the active '#' (or start of word in dedicated mode) sits. */
  activeHashStart = -1;

  constructor(private el: ElementRef<HTMLElement>) {}

  // ── Helpers ───────────────────────────────────────────────────────────────

  private get inputEl(): HTMLInputElement | HTMLTextAreaElement | null {
    const n = this.el.nativeElement;
    return n instanceof HTMLInputElement || n instanceof HTMLTextAreaElement ? n : null;
  }

  getText(): string {
    return this.inputEl?.value ?? this.el.nativeElement.textContent ?? '';
  }

  setText(value: string): void {
    const input = this.inputEl;
    if (input) { input.value = value; return; }
    this.el.nativeElement.textContent = value;
  }

  private getCaretOffset(): number {
    const input = this.inputEl;
    if (input) return input.selectionStart ?? input.value.length;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return this.getText().length;
    const range = sel.getRangeAt(0);
    const pre   = range.cloneRange();
    pre.selectNodeContents(this.el.nativeElement);
    pre.setEnd(range.endContainer, range.endOffset);
    return pre.toString().length;
  }

  // ── Input listener ────────────────────────────────────────────────────────

  @HostListener('input', ['$event'])
  onInput(event?: Event): void {
    const text  = this.getText();
    const caret = this.getCaretOffset();

    this.valueChange.emit(text);

    if (this.hashtagMode === 'dedicated') {
      // Only in dedicated mode — in inline mode (captions) a space is ordinary
      // typing and must never commit a tag.
      if (/[\s,]$/.test(text)) {
        setTimeout(() => this.separatorTyped.emit(event ?? new Event('input')));
      }
      this.handleDedicatedMode(text, caret);
    } else {
      this.handleInlineMode(text, caret);
    }
  }

  /**
   * Dedicated mode: the whole input is for hashtags.
   * Show suggestions for whatever the user is currently typing,
   * stripping any leading '#' they may have typed themselves.
   */
  private handleDedicatedMode(text: string, caret: number): void {
    // Find the start of the current "word" at the caret position
    let wordStart = caret;
    for (let i = caret - 1; i >= 0; i--) {
      if (/\s/.test(text[i])) { wordStart = i + 1; break; }
      if (i === 0) { wordStart = 0; }
    }

    const partial = text.slice(wordStart, caret).replace(/^#+/, '');

    if (!partial && text.trim() === '') {
      // Input is empty — show trending (search with empty string)
      this.activeHashStart = 0;
      this.hashtagDropdown.emit(true);
      this.hashtagSearch.emit('');
      return;
    }

    if (!partial) {
      // Caret is at whitespace or after a completed tag
      if (this.activeHashStart !== -1) {
        this.activeHashStart = -1;
        this.hashtagDropdown.emit(false);
      }
      return;
    }

    // Only word chars in a partial tag
    if (/[^\w]/.test(partial)) {
      this.activeHashStart = -1;
      this.hashtagDropdown.emit(false);
      return;
    }

    this.activeHashStart = wordStart;
    this.hashtagDropdown.emit(true);
    this.hashtagSearch.emit(partial);
  }

  /**
   * Inline mode: free-text field, only trigger after '#'.
   */
  private handleInlineMode(text: string, caret: number): void {
    let hashStart = -1;
    for (let i = caret - 1; i >= 0; i--) {
      if (text[i] === '#') { hashStart = i; break; }
      if (/\s/.test(text[i])) break;
    }

    if (hashStart === -1) {
      if (this.activeHashStart !== -1) {
        this.activeHashStart = -1;
        this.hashtagDropdown.emit(false);
      }
      return;
    }

    const partial = text.slice(hashStart + 1, caret);
    if (/[^\w]/.test(partial)) {
      this.activeHashStart = -1;
      this.hashtagDropdown.emit(false);
      return;
    }

    this.activeHashStart = hashStart;
    this.hashtagDropdown.emit(true);
    this.hashtagSearch.emit(partial);
  }

  // ── Keyboard & blur ───────────────────────────────────────────────────────

  @HostListener('keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.activeHashStart !== -1) {
      this.activeHashStart = -1;
      this.hashtagDropdown.emit(false);
    }
  }

  @HostListener('blur')
  onBlur(): void {
    // Delay so mousedown on a dropdown item fires before we close
    setTimeout(() => {
      if (this.activeHashStart !== -1) {
        this.activeHashStart = -1;
        this.hashtagDropdown.emit(false);
      }
    }, 200);
  }

  // ── Insert selected tag ───────────────────────────────────────────────────

  /**
   * Called by the parent when the user picks a suggestion.
   * Replaces the partial text (e.g. "#lon" or "lon") with "#london "
   * and resets directive state.
   *
   * @param tag  normalised tag WITHOUT '#', e.g. "london"
   * @returns    new full value of the input
   */
  insertHashtag(tag: string): string {
    const caret = this.getCaretOffset();
    const text  = this.getText();

    // In dedicated mode activeHashStart may be -1 on first pick (empty input).
    // Fall back to replacing everything before the caret in that case.
    const start  = this.activeHashStart >= 0 ? this.activeHashStart : 0;
    const before = text.slice(0, start);
    const after  = text.slice(caret);
    const insert = `#${tag} `;
    const newVal = `${before}${insert}${after}`;

    this.setText(newVal);

    const input = this.inputEl;
    if (input) {
      const pos = before.length + insert.length;
      input.setSelectionRange(pos, pos);
      // Dispatch input event so ngModel stays in sync
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    this.activeHashStart = -1;
    this.hashtagDropdown.emit(false);
    this.valueChange.emit(newVal);
    return newVal;
  }

  /** Programmatically close the dropdown without inserting anything. */
  closeDropdown(): void {
    this.activeHashStart = -1;
    this.hashtagDropdown.emit(false);
  }
}